import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Phone, FileText, Pencil, Plus, ShieldAlert, Trash2, AlertTriangle, Wallet, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg, inr, fmtDate, today, openFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, StatCard, Field, TableWrap, Empty, th, td, tr } from "@/components/common";
import { DriverAvatar, DriverDialog, FilePick, LedgerDialog } from "@/pages/Drivers";
import { download } from "@/components/gst";

const CATEGORIES = ["Cargo Damage", "Accidents", "Missing Goods", "Traffic Fines / Challan", "Fuel Theft / Excess", "Other"];

function LossDialog({ open, onClose, driverId, trips, onSaved }) {
  const [vehicles, setVehicles] = useState([]);
  const [f, setF] = useState({ date: today(), amount: "", category: CATEGORIES[0], booking_id: "", vehicle_id: "", description: "", proof_file_ids: [] });
  useEffect(() => { if (open) { setF({ date: today(), amount: "", category: CATEGORIES[0], booking_id: "", vehicle_id: "", description: "", proof_file_ids: [] }); api.get("/vehicles").then((r) => setVehicles(r.data)); } }, [open]);
  const save = async () => {
    if (!f.amount || Number(f.amount) <= 0) return toast.error("Enter a valid amount");
    try {
      await api.post(`/drivers/${driverId}/losses`, { ...f, amount: Number(f.amount), booking_id: f.booking_id || null, vehicle_id: f.vehicle_id || null });
      toast.success("Loss / penalty recorded");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const addProof = (id) => setF((p) => (p.proof_file_ids.length < 3 ? { ...p, proof_file_ids: [...p.proof_file_ids, id] } : p));
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card" data-testid="loss-dialog">
        <DialogHeader><DialogTitle>Add Loss / Penalty</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Date *"><Input data-testid="loss-date-input" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
          <Field label="Amount (₹) *"><Input data-testid="loss-amount-input" type="number" min="1" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>
          <Field label="Category *" className="col-span-2">
            <select data-testid="loss-category-select" className="native-select" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Related Trip (LR)">
            <select data-testid="loss-trip-select" className="native-select" value={f.booking_id} onChange={(e) => setF({ ...f, booking_id: e.target.value })}>
              <option value="">— None —</option>
              {trips.map((t) => <option key={t.id} value={t.id}>{t.lr_number} · {t.pickup_location} → {t.drop_location}</option>)}
            </select>
          </Field>
          <Field label="Related Vehicle">
            <select data-testid="loss-vehicle-select" className="native-select" value={f.vehicle_id} onChange={(e) => setF({ ...f, vehicle_id: e.target.value })}>
              <option value="">— None —</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.vehicle_number}</option>)}
            </select>
          </Field>
          <Field label="Description / Remarks" className="col-span-2"><Textarea data-testid="loss-description-input" rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
          <div className="col-span-2 space-y-2">
            <div className="label-xs">Proof (receipt / damage photo / challan copy) — up to 3</div>
            {f.proof_file_ids.length < 3 && (
              <FilePick label="Upload Proof" refType="document" testid={`loss-proof-btn-${f.proof_file_ids.length}`} value={null} onChange={addProof} />
            )}
            <div className="flex flex-wrap gap-2">
              {f.proof_file_ids.map((id, i) => (
                <span key={id} className="flex items-center gap-1 rounded-full border bg-secondary px-2.5 py-1 text-xs">
                  <FileText className="h-3 w-3" /> Proof {i + 1}
                  <button type="button" data-testid={`loss-proof-remove-${i}`} onClick={() => setF({ ...f, proof_file_ids: f.proof_file_ids.filter((x) => x !== id) })} className="ml-1 text-muted-foreground hover:text-rose-600">×</button>
                </span>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter><Button data-testid="loss-save-btn" onClick={save}>Save Loss Entry</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DriverProfile() {
  const { id } = useParams();
  const { role } = useAuth();
  const isAdmin = role === "admin";
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [lossOpen, setLossOpen] = useState(false);
  const [ledger, setLedger] = useState(null);
  const openLedger = () => api.get("/drivers").then((r) => setLedger(r.data.find((x) => x.id === id) || null)).catch((e) => toast.error(errMsg(e)));
  const load = useCallback(() => api.get(`/drivers/${id}/history`).then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); navigate("/drivers"); }), [id]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  const feed = useMemo(() => {
    if (!d) return [];
    return [
      ...d.transactions.map((t) => ({ kind: t.type === "advance" ? "Advance Given" : t.type === "recovery" ? "Recovered from Driver" : t.type === "salary" ? `Monthly Salary · ${t.month}` : "Salary Paid", date: t.date, amount: t.amount, note: t.note, sign: t.type === "advance" || (t.type === "salary_payment" && !t.booking_id) ? 1 : -1 })),
      ...d.losses.map((l) => ({ kind: `Loss · ${l.category}`, date: l.date, amount: l.amount, note: l.description, sign: 1 })),
    ].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [d]);
  if (!d) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const { driver, trips, vehicle_assignments: assigns, transactions, losses, totals, is_admin } = d;
  const money = (v) => (v == null ? "-" : Number(v).toLocaleString("en-IN") + " km");
  const delLoss = async (l) => {
    if (!window.confirm("Delete this loss entry?")) return;
    try { await api.delete(`/losses/${l.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <button data-testid="driver-back-btn" onClick={() => navigate("/drivers")} className="mb-3 flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"><ArrowLeft className="h-3.5 w-3.5" /> All Drivers</button>

      {driver.employment_status === "Blacklisted" && (
        <div data-testid="blacklist-banner" className="mb-4 flex items-start gap-3 rounded-xl bg-rose-600 p-4 text-white fade-up">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <div className="font-bold tracking-wide">BLACKLISTED DRIVER</div>
            <div className="text-sm text-rose-100">Reason: {driver.blacklist_reason}{driver.blacklisted_at && ` · since ${fmtDate(driver.blacklisted_at)}`}. This driver is blocked from all new bookings and vehicle assignments.</div>
          </div>
        </div>
      )}

      <div className="card-base mb-6 flex flex-col gap-4 p-5 fade-up sm:flex-row sm:items-center md:p-6">
        <div className="flex items-center gap-4">
          <div className="[&>img]:h-16 [&>img]:w-16 [&>div]:h-16 [&>div]:w-16 [&>div]:text-xl"><DriverAvatar fileId={driver.driver_photo_id} name={driver.name} /></div>
          <div>
            <div className="flex items-center gap-2"><h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="driver-profile-name">{driver.name}</h1><StatusBadge status={driver.employment_status} testid="driver-profile-status" /></div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <a href={`tel:${driver.phone}`} data-testid="driver-phone-link" className="flex items-center gap-1 text-primary hover:underline"><Phone className="h-3.5 w-3.5" />{driver.phone}</a>
              <span className="font-mono text-xs">DL {driver.license_number}</span>
              {driver.license_photo_id && <button data-testid="view-license-btn" onClick={() => openFile(driver.license_photo_id)} className="flex items-center gap-1 text-primary hover:underline"><FileText className="h-3.5 w-3.5" />View License</button>}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {(driver.family_contacts || []).map((c) => `Kin: ${c.name} (${c.phone})`).join(" · ")}
            </div>
          </div>
        </div>
        <div className="flex gap-2 sm:ml-auto">
          {["admin", "accounts"].includes(role) && <Button variant="outline" data-testid="driver-ledger-btn" onClick={openLedger}><Wallet className="mr-1 h-4 w-4" />Driver Ledger</Button>}
          <Button variant="outline" data-testid="driver-add-loss-btn" onClick={() => setLossOpen(true)}><AlertTriangle className="mr-1 h-4 w-4 text-amber-600" />Add Loss / Penalty</Button>
          {isAdmin && <Button variant="outline" data-testid="driver-edit-btn" onClick={() => setEditOpen(true)}><Pencil className="mr-1 h-4 w-4" />Edit</Button>}
        </div>
      </div>

      <Tabs defaultValue={["admin", "accounts"].includes(role) ? "financial" : "trips"}>
        <TabsList className="mb-5 flex-wrap">
          <TabsTrigger value="trips" data-testid="tab-trip-history">Trip History</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="tab-vehicle-history">Vehicle History</TabsTrigger>
          {is_admin && <TabsTrigger value="financial" data-testid="tab-financial-history">Driver Ledger</TabsTrigger>}
          <TabsTrigger value="losses" data-testid="tab-losses">Loss / Damage ({losses.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="trips">
          <TableWrap>
            <thead><tr>{["LR No", "Date", "Route", "Material", "Vehicle", ...(is_admin ? ["Freight"] : []), "Status"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
            <tbody>
              {trips.map((t) => (
                <tr key={t.id} className={`${tr} cursor-pointer`} onClick={() => navigate(`/bookings/${t.id}`)} data-testid={`history-trip-${t.lr_number}`}>
                  <td className={`${td} font-mono font-semibold text-primary`}>{t.lr_number}</td>
                  <td className={td}>{fmtDate(t.booking_date)}{t.delivered_at && <div className="text-xs text-muted-foreground">Loading Point {fmtDate(t.delivered_at)}</div>}</td>
                  <td className={td}>{t.pickup_location} → {t.drop_location}</td>
                  <td className={`${td} text-muted-foreground`}>{t.material_type}</td>
                  <td className={`${td} font-mono text-xs`}>{t.vehicle_number || "-"}</td>
                  {is_admin && <td className={`${td} amount`}>{inr(t.freight_amount)}</td>}
                  <td className={td}><StatusBadge status={t.status} /></td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          {trips.length === 0 && <Empty testid="history-trips-empty" text="No trips yet" />}
        </TabsContent>

        <TabsContent value="vehicles">
          <TableWrap>
            <thead><tr>{["Vehicle", "Start KM (Joining)", "Left Vehicle KM", "From", "To", "Status"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
            <tbody>
              {assigns.map((a, i) => (
                <tr key={a.id || i} className={tr} data-testid={`history-vehicle-${i}`}>
                  <td className={`${td} font-mono font-semibold`}>{a.vehicle_number}</td>
                  <td className={`${td} amount`}>{money(a.start_km)}</td>
                  <td className={`${td} amount`}>{a.end_km != null ? money(a.end_km) : "—"}</td>
                  <td className={td}>{fmtDate(a.started_at)}</td>
                  <td className={td}>{a.ended_at ? fmtDate(a.ended_at) : "—"}</td>
                  <td className={td}>{a.ended_at ? <span className="text-xs text-muted-foreground">Completed</span> : <span className="rounded bg-accent px-2 py-0.5 text-xs font-semibold text-accent-foreground">Current</span>}</td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          {assigns.length === 0 && <Empty testid="history-vehicles-empty" text="No vehicle assignments recorded. Assign a vehicle to start tracking." />}
        </TabsContent>

        {is_admin && (
          <TabsContent value="financial">
            <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-3">
              <StatCard testid="fin-advances" label="Advances Given" value={inr(totals.advances)} />
              <StatCard testid="fin-driver-expenses" label="Expenses Paid by Driver" value={inr(totals.driver_expenses || 0)} hint="Approved driver-paid bills — to be reimbursed / adjusted" accent="text-sky-600" />
              <StatCard testid="fin-company-expenses" label="Company-Paid Expenses" value={inr(totals.company_expenses || 0)} hint="Paid directly by company — excluded from settlement" />
              <StatCard testid="fin-allowances" label="Trip Allowances" value={inr(totals.allowances)} />
              <StatCard testid="fin-salary-earned" label="Monthly Salary (jama)" value={inr(totals.salary_earned || 0)} accent="text-sky-600" />
              <StatCard testid="fin-salary" label="Salary Paid" value={inr(totals.salary_paid)} accent="text-emerald-600" />
              <StatCard testid="fin-losses" label="Loss Deductions" value={inr(totals.losses)} accent="text-rose-600" />
            </div>
            <div className={`mb-5 flex items-center justify-between rounded-xl border p-4 ${totals.net_balance > 0 ? "border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/30" : "border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30"}`}>
              <div>
                <div className="text-sm font-semibold" data-testid="fin-net-label">{totals.net_balance > 0 ? "Net Balance — Receivable from Driver" : "Net Balance — Payable to Driver"}</div>
                <div className="text-xs text-muted-foreground">Advances + Losses + Monthly Salary Paid − Monthly Salary (jama) − Driver-Paid Expenses − Recovered</div>
              </div>
              <div className={`amount text-xl font-bold ${totals.net_balance > 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid="fin-net-balance">{inr(Math.abs(totals.net_balance))}</div>
            </div>
            <div className="card-base">
              <div className="flex items-center justify-between border-b px-5 py-3 text-sm font-semibold">All Entries
                <Button size="sm" variant="outline" data-testid="driver-ledger-pdf-btn" onClick={() => download(`/drivers/${id}/ledger.pdf`, {}, `Driver_Ledger_${driver.name}.pdf`)}><Download className="mr-1 h-3.5 w-3.5" />Download PDF</Button>
              </div>
              <div className="divide-y">
                {feed.map((e, i) => (
                  <div key={i} className="flex items-center justify-between px-5 py-2.5 text-sm" data-testid={`fin-entry-${i}`}>
                    <div><div className="font-medium">{e.kind}</div><div className="text-xs text-muted-foreground">{fmtDate(e.date)}{e.note && ` · ${e.note}`}</div></div>
                    <span className={`amount ${e.sign > 0 ? "text-rose-600" : "text-emerald-600"}`}>{e.sign > 0 ? "+" : "−"}{inr(e.amount)}</span>
                  </div>
                ))}
                {feed.length === 0 && <Empty text="No financial entries yet" />}
              </div>
            </div>
          </TabsContent>
        )}

        <TabsContent value="losses">
          <div className="mb-4 flex justify-end"><Button data-testid="losses-add-btn" onClick={() => setLossOpen(true)}><Plus className="mr-1 h-4 w-4" />Add Loss / Penalty</Button></div>
          <div className="space-y-3">
            {losses.map((l) => (
              <div key={l.id} data-testid={`loss-card-${l.id}`} className="card-base flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-950/40"><AlertTriangle className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{l.category} <span className="ml-1 amount text-rose-600">{inr(l.amount)}</span></div>
                  <div className="text-xs text-muted-foreground">{fmtDate(l.date)} · {l.lr_number ? <Link className="text-primary hover:underline" to={`/bookings/${l.booking_id}`}>{l.lr_number}</Link> : "No trip"}{l.vehicle_number && ` · ${l.vehicle_number}`} · by {l.created_by_name}</div>
                  {l.description && <div className="mt-1 text-sm">{l.description}</div>}
                </div>
                <div className="flex items-center gap-2">
                  {l.proof_file_ids.map((fid, i) => <button key={fid} data-testid={`loss-proof-view-${i}`} onClick={() => openFile(fid)} className="rounded border px-2 py-1 text-xs font-medium text-primary hover:bg-accent">Proof {i + 1}</button>)}
                  {isAdmin && <button data-testid={`loss-delete-${l.id}`} onClick={() => delLoss(l)} className="rounded p-1.5 text-muted-foreground hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
                </div>
              </div>
            ))}
            {losses.length === 0 && <div className="card-base"><Empty testid="losses-empty" text="No loss or penalty entries" /></div>}
          </div>
        </TabsContent>
      </Tabs>

      <DriverDialog open={editOpen} onClose={() => setEditOpen(false)} initial={driver} onSaved={load} />
      <LossDialog open={lossOpen} onClose={() => setLossOpen(false)} driverId={id} trips={trips} onSaved={load} />
      <LedgerDialog driver={ledger} onClose={() => setLedger(null)} onSaved={() => { load(); openLedger(); }} />
    </div>
  );
}
